import { parse } from 'querystring';
import { isScreenshotMode } from '../../../common/generic/utilities/debug-mode';
import { ToggledUIComponents } from '../../../common/generic/utilities/types';
import { isViewSettingOn, ViewSettingKey } from '../../../common/generic/view-settings/registry';
import { ViewSettingsBlob } from '../../../common/generic/utilities/storage';
import { StateTree } from '../reducers';

export function loggedInUser(state: StateTree) {
  return state.user.user;
}

export function isRequestOngoing(state: StateTree, requestId: string) {
  return getRequestsTree(state).ongoingRequests.indexOf(requestId) > -1;
}

export const userInfoRequestId = 'GET_TOKEN';
export function isUserInfoRequestOngoing(state: StateTree) {
  return isRequestOngoing(state, userInfoRequestId);
}

function getRequestsTree(state: StateTree) {
  return state.request;
}

/**
 * Return undefined if no error exists for request.
 */
export function getRequestError(state: StateTree, requestId: string): string | undefined {
  const errorObject = getRequestsTree(state).failedRequests.find(r => r.id === requestId);
  return errorObject && errorObject.error;
}

export function isLoadingResults(_state: StateTree) {
  // TODO Refactor code to NOT use this
  return false;
}

export function getValueSet(state: StateTree) {
  return state.selections.valueSet;
}

interface VersionAndRelease {
  version: string;
  release: string | null;
}

/**
 * Use this selector outside the MainRoot component where version and release may not be loaded yet
 */
export function getVersionAndReleaseIfExists(state: StateTree): Partial<VersionAndRelease> {
  const params = parse(window.location.search.replace('?', ''));
  const queryRelease = params.release as string | undefined;
  const queryVersion = params.version as string | undefined;
  if (isScreenshotMode() && queryRelease && queryVersion) {
    return {
      version: queryVersion,
      release: queryRelease,
    };
  }
  const { selectedModelVersion, selectedRelease } = state.selections;
  return {
    version: selectedModelVersion,
    release: selectedRelease,
  };
}

export function getVersionAndRelease(state: StateTree): VersionAndRelease {
  const { version, release } = getVersionAndReleaseIfExists(state);
  if (version === undefined || release === undefined) {
    throw new Error(`Model version or release not found`);
  }
  return { version, release };
}

export function getIsModelVersionOrReleaseUninitialized(state: StateTree): boolean {
  const { selectedModelVersion, selectedRelease } = state.selections;
  return selectedModelVersion === undefined || selectedRelease === undefined;
}

export function getDefaultVersionAndReleaseIfExists(state: StateTree) {
  const { defaultRelease, defaultVersion } = state.selections;
  return defaultRelease && defaultVersion ? { defaultRelease, defaultVersion } : undefined;
}

export function getDefaultRelease(state: StateTree) {
  const { defaultRelease } = state.selections;
  if (defaultRelease === undefined) {
    throw new Error(`Default release not found`);
  }
  return defaultRelease;
}

export function getDefaultVersion(state: StateTree) {
  const { defaultVersion } = state.selections;
  if (defaultVersion === undefined) {
    throw new Error(`Default model version not found`);
  }
  return defaultVersion;
}

export function getValueSetName(state: StateTree) {
  return state.selections.valueSetName;
}

// PLT-4774: getProductsByCode (and the dependent getSortedProductsList /
// getProductTreeCounts / getTopLevelProducts selectors below) were
// removed. The slim product hierarchy is now cached by React Query —
// use `useProductsByCode()` / `useSortedProductsList()` /
// `useProductTreeCounts()` / `useTopLevelProducts()` from
// `src/ui/api/model/input/products` instead.

export function getImpactLabelsById(state: StateTree) {
  return state.data.impactLabels;
}

export function getImpactSizeFactors(state: StateTree) {
  return state.data.impactSizeFactors;
}

function getUserTree(state: StateTree) {
  return state.user;
}

function getModalTree(state: StateTree) {
  return state.modal;
}

/**
 * Whether a single per-feature view setting is enabled (PLT-4824).
 *
 * NOTE: this only reflects the toggle's stored value — callers that gate UI
 * on it should additionally re-check `isTeamUser(currentUser)` (defence in
 * depth, so a stale localStorage flag on a non-team account is a no-op). The
 * `useViewSetting` hook does this re-check; prefer it over reading this
 * selector directly.
 */
export function getViewSetting(state: StateTree, key: ViewSettingKey): boolean {
  return isViewSettingOn(state.selections.viewSettings, key);
}

export function getAllViewSettings(state: StateTree): ViewSettingsBlob {
  return state.selections.viewSettings;
}

/**
 * Whether the View settings modal (PLT-4824) is currently open. Read by the
 * modal mounted at the app root; written by the user-menu link, the status
 * bar item, and the `Ctrl+*` global hotkey.
 */
export function getViewSettingsModalOpen(state: StateTree): boolean {
  return state.selections.viewSettingsModalOpen;
}

/**
 * Whether the chat internal-mode debug panel is enabled. NOTE: this only
 * reflects the toggle's stored value — callers that gate UI on it should
 * additionally re-check `isTeamUser(currentUser)` (defence in depth, so a
 * stale localStorage flag on a non-team account is a no-op). See PLT-4475.
 */
export function getChatInternalMode(state: StateTree) {
  return state.selections.chatInternalMode;
}

export function getOpenModal(state: StateTree) {
  return getModalTree(state).open;
}

export function getValuesSelectorOpen(state: StateTree) {
  return state.selections.valuesSelectorOpen;
}

export function getOfficialTime(state: StateTree) {
  return state.data.time;
}

export function getToggledComponents(state: StateTree) {
  const toggledUIComponents = getUserTree(state).toggledUIComponents;
  if (!toggledUIComponents) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {} as ToggledUIComponents;
  }
  return toggledUIComponents;
}

// `getStatistics` previously aggregated tag and phrase counts by walking
// every heavy product in Redux. Replaced by the `useGetProductStatistics`
// hook, which calls a server-computed endpoint. See PLT-4774.
